import axios from './axios';

/**
 * 资源类型枚举（与后端保持一致）
 */
export enum ResourceType {
  BUDGET = '预算表',
  BUDGET_ITEM = '预算项',
  FILM = '影片项目',
  BREAKDOWN = '分场表',
  SCENE = '场次',
  SCHEDULE = '拍摄计划',
  SLOT = '拍摄任务',
}

/**
 * 权限类型枚举
 */
export enum PermissionType {
  VIEW = '可见',
  EDIT = '可编辑',
  SHARE = '可分享',
  COPY = '可复制',
  MANAGE_MEMBERS = '可管理成员',
  TRANSFER_OWNERSHIP = '可转移所有权',
}

/**
 * 被授权者类型枚举
 */
export enum GranteeType {
  USER = '用户',
  ORG = '组织',
  DEPARTMENT = '部门',
}

export enum PermissionRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  EDITOR = 'EDITOR',
  COPY = 'COPY',
  VIEWER = 'VIEWER',
}

/**
 * 用户权限接口
 */
export interface UserPermissions {
  permissionID: string;
  resourceType: ResourceType;
  resourceID: string;
  granteeType: GranteeType;
  granteeID: string;
  role: PermissionRole;
  creatorID?: string;
  createTime: Date | string;
  updateTime: Date | string;
}

/**
 * API响应类型
 */
export interface UserPermissionsResponse {
  data: UserPermissions[];
  status: number;
  statusText: string;
}

/**
 * 根据资源类型和资源ID获取权限
 * @param resourceType 资源类型
 * @param resourceID 资源ID
 * @returns 权限列表
 */
export const getPermissionsByResource = (
  resourceType: ResourceType,
  resourceID: string
): Promise<UserPermissionsResponse> => {
  return axios.get<UserPermissions[]>(
    `/api/user-permissions/resource/${resourceType}/${resourceID}`
  ).catch((error) => {
    // 404 表示没有权限记录，这是正常的，返回空数组
    if (error.response?.status === 404) {
      return { data: [], status: 200, statusText: 'OK' };
    }
    // 其他错误继续抛出
    throw error;
  });
};

/**
 * 批量查询权限
 * @param params 查询参数
 * @returns 权限列表
 */
export const getPermissionsBatch = (params?: {
  resourceType?: ResourceType;
  resourceIDs?: string[]; // 资源ID数组
  permissionIDs?: string[]; // 权限ID数组
}): Promise<UserPermissionsResponse> => {
  // 使用 POST body，避免大量 resourceIDs 导致 URL 超长被 Nginx/HTTP2 拒绝
  const body: {
    resourceType?: ResourceType;
    resourceIDs?: string;
    permissionIDs?: string;
  } = {};

  if (params?.resourceType) {
    body.resourceType = params.resourceType;
  }

  if (params?.resourceIDs && params.resourceIDs.length > 0) {
    body.resourceIDs = params.resourceIDs.join(',');
  }

  if (params?.permissionIDs && params.permissionIDs.length > 0) {
    body.permissionIDs = params.permissionIDs.join(',');
  }

  return axios.post<UserPermissions[]>('/api/user-permissions/batch/query', body);
};

/**
 * 批量获取权限（按资源类型和多个资源ID）
 * 使用批量查询接口，然后将结果按resourceID分组
 * @param resourceType 资源类型
 * @param resourceIDs 资源ID数组
 * @returns 权限列表（按resourceID分组）
 */
export const getPermissionsBatchByResourceIDs = async (
  resourceType: ResourceType,
  resourceIDs: string[]
): Promise<Map<string, UserPermissions[]>> => {
  if (resourceIDs.length === 0) {
    return new Map();
  }

  try {
    // 使用批量查询接口
    const response = await getPermissionsBatch({
      resourceType,
      resourceIDs,
    });

    // 将结果按resourceID分组
    const permissionsMap = new Map<string, UserPermissions[]>();
    resourceIDs.forEach((resourceID) => {
      permissionsMap.set(resourceID, []);
    });

    const returnedPermissions: UserPermissions[] = response?.data || [];
    
    // 将返回的权限按resourceID分组
    returnedPermissions.forEach((permission) => {
      const existing = permissionsMap.get(permission.resourceID) || [];
      existing.push(permission);
      permissionsMap.set(permission.resourceID, existing);
    });

    return permissionsMap;
  } catch (error: any) {
    // 如果批量接口返回 404，说明接口不存在，记录错误并返回空结果
    if (error.response?.status === 404) {
      console.error(`批量查询权限接口不存在 (404): /api/user-permissions/batch/query`);
      
      // 返回空结果（所有资源都没有权限记录）
      const permissionsMap = new Map<string, UserPermissions[]>();
      resourceIDs.forEach((resourceID) => {
        permissionsMap.set(resourceID, []);
      });
      return permissionsMap;
    }
    
    // 其他错误，记录并返回空结果
    console.error(`批量查询权限失败:`, error);
    const permissionsMap = new Map<string, UserPermissions[]>();
    resourceIDs.forEach((resourceID) => {
      permissionsMap.set(resourceID, []);
    });
    return permissionsMap;
  }
};

/**
 * 根据查询条件获取权限列表
 * @param params 查询参数
 * @returns 权限列表
 */
export const getPermissionsByFilters = (params?: {
  role?: Exclude<PermissionRole, PermissionRole.OWNER>;
  resourceType?: ResourceType;
  resourceID?: string;
  granteeType?: GranteeType;
  granteeID?: string;
}): Promise<UserPermissionsResponse> => {
  return axios.get<UserPermissions[]>('/api/user-permissions', { params });
};

/**
 * 查询某用户作为被授权者的权限记录（完整列表，后端 GET /api/user-permissions 筛选）
 */
export const getPermissionsListForGranteeUser = async (
  userID: string,
  resourceType: ResourceType
): Promise<UserPermissions[]> => {
  const response = await getPermissionsByFilters({
    granteeType: GranteeType.USER,
    granteeID: userID,
    resourceType,
  });
  return response?.data || [];
};

/** 将权限列表按 resourceID 分组（同一资源多行全部保留） */
export const groupUserPermissionsByResourceId = (
  list: UserPermissions[]
): Map<string, UserPermissions[]> => {
  const m = new Map<string, UserPermissions[]>();
  for (const p of list) {
    if (!p.resourceID) continue;
    const arr = m.get(p.resourceID) ?? [];
    arr.push(p);
    m.set(p.resourceID, arr);
  }
  return m;
};

/**
 * 查询某用户作为被授权者的权限，返回指定 resourceType 下的 resourceID 列表（去重）
 */
export const getPermissionsBatchByUserID = async (
  userID: string,
  resourceType: ResourceType
): Promise<string[]> => {
  const list = await getPermissionsListForGranteeUser(userID, resourceType);
  return Array.from(new Set(list.map((p) => p.resourceID).filter(Boolean) as string[]));
};

/**
 * 获取被授权者对资源的所有权限
 * @param granteeType 被授权者类型
 * @param granteeID 被授权者ID
 * @param resourceType 资源类型
 * @param resourceID 资源ID
 * @returns 权限对象
 */
export const getGranteePermissions = (
  granteeType: GranteeType,
  granteeID: string,
  resourceType: ResourceType,
  resourceID: string
): Promise<{
  data: {
    role: PermissionRole | null;
  };
  status: number;
  statusText: string;
}> => {
  return axios.get(
    `/api/user-permissions/permissions/${granteeType}/${granteeID}/${resourceType}/${resourceID}`
  );
};

/**
 * 检查被授权者是否有特定权限
 * @param granteeType 被授权者类型
 * @param granteeID 被授权者ID
 * @param resourceType 资源类型
 * @param resourceID 资源ID
 * @param permissionType 权限类型
 * @returns 是否有权限
 */
export const checkPermission = (
  granteeType: GranteeType,
  granteeID: string,
  resourceType: ResourceType,
  resourceID: string,
  permissionType: PermissionType
): Promise<{
  data: { hasPermission: boolean };
  status: number;
  statusText: string;
}> => {
  return axios.get(
    `/api/user-permissions/check/${granteeType}/${granteeID}/${resourceType}/${resourceID}/${permissionType}`
  );
};

/**
 * 创建权限 DTO
 */
export interface CreateUserPermissionsParams {
  resourceType: ResourceType;
  resourceID: string;
  granteeType: GranteeType;
  granteeID: string;
  role: Exclude<PermissionRole, PermissionRole.OWNER>;
  creatorID?: string;
}

/**
 * 更新权限 DTO
 */
export interface UpdateUserPermissionsParams {
  role?: Exclude<PermissionRole, PermissionRole.OWNER>;
}

/**
 * 设置权限 DTO（upsert操作）
 */
export interface SetUserPermissionsParams {
  resourceType: ResourceType;
  resourceID: string;
  granteeType: GranteeType;
  granteeID: string;
  role: Exclude<PermissionRole, PermissionRole.OWNER>;
  creatorID?: string;
}

/**
 * 创建权限
 */
export const createUserPermission = (
  data: CreateUserPermissionsParams
): Promise<{
  data: UserPermissions;
  status: number;
  statusText: string;
}> => {
  return axios.post<UserPermissions>('/api/user-permissions', data);
};

/**
 * 更新权限
 */
export const updateUserPermission = (
  permissionID: string,
  data: UpdateUserPermissionsParams
): Promise<{
  data: UserPermissions;
  status: number;
  statusText: string;
}> => {
  return axios.put<UserPermissions>(`/api/user-permissions/${permissionID}`, data);
};

/**
 * 设置权限（upsert操作）
 */
export const setUserPermission = (
  data: SetUserPermissionsParams
): Promise<{
  data: UserPermissions;
  status: number;
  statusText: string;
}> => {
  return axios.put<UserPermissions>('/api/user-permissions/set', data);
};

export const revokeUserPermission = (data: {
  resourceType: ResourceType;
  resourceID: string;
  granteeType: GranteeType;
  granteeID: string;
}): Promise<{ data: { message: string }; status: number; statusText: string }> => {
  return axios.delete('/api/user-permissions/revoke', { data });
};

/**
 * 删除权限
 */
export const deleteUserPermission = (
  permissionID: string
): Promise<{
  data: { message: string };
  status: number;
  statusText: string;
}> => {
  return axios.delete(`/api/user-permissions/${permissionID}`);
};

/**
 * 删除资源的所有权限
 */
export const deletePermissionsByResource = (
  resourceType: ResourceType,
  resourceID: string
): Promise<{
  data: { message: string };
  status: number;
  statusText: string;
}> => {
  return axios.delete(`/api/user-permissions/resource/${resourceType}/${resourceID}`);
};

/**
 * 批量更新权限 DTO
 */
export interface BatchUpdatePermissionItem {
  permissionID: string;
  role: Exclude<PermissionRole, PermissionRole.OWNER>;
}

/**
 * 批量更新权限
 */
export const updatePermissionsBatch = (
  updates: BatchUpdatePermissionItem[]
): Promise<{
  data: UserPermissions[];
  status: number;
  statusText: string;
}> => {
  return axios.put<UserPermissions[]>('/api/user-permissions/batch', { updates });
};

/**
 * 批量删除权限
 */
export const deletePermissionsBatch = (
  permissionIDs: string[]
): Promise<{
  data: { message: string; deletedCount: number };
  status: number;
  statusText: string;
}> => {
  return axios.delete('/api/user-permissions/batch', {
    data: { permissionIDs },
  });
};
